import { motion } from "framer-motion";
import { Flame, Megaphone, ShieldQuestion, ListChecks, Network, Star } from "lucide-react";
import { JoinForm } from "./JoinForm";

export const VOICES = [
  { name: "The Instigator", voice: "the voice of inspiration", text: "Asks \"what if?\" and \"why not?\", and gets us to try.", icon: Flame, color: "text-bloom" },
  { name: "The Cheerleader", voice: "the voice of motivation", text: "Believes in people, champions the work and won't let anyone down.", icon: Megaphone, color: "text-gold" },
  { name: "The Doubter", voice: "the voice of reason", text: "Asks the hard questions and sees problems before they arise.", icon: ShieldQuestion, color: "text-primary" },
  { name: "The Taskmaster", voice: "the voice of progress", text: "Keeps momentum, so deadlines are met and goals are reached.", icon: ListChecks, color: "text-leaf" },
  { name: "The Connector", voice: "the voice of cooperation and community", text: "Opens new avenues and brings in people and places we can't reach.", icon: Network, color: "text-bloom" },
  { name: "The Example", voice: "the voice of true authority", text: "Has been there and done that — a mentor and a North Star.", icon: Star, color: "text-gold" },
];

export const JoinVillage = ({ headingAs: Heading = "h2" }) => (
  <section id="join" className="relative bg-muted/60 py-28 lg:py-36" data-testid="join-village-section">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <div className="grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-6">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">An invitation</p>
          <Heading className="mt-6 font-display text-4xl font-bold leading-tight tracking-tight text-foreground sm:text-5xl">
            Come and help <span className="text-primary">grow the village.</span>
          </Heading>
        </div>
        <div className="space-y-5 text-base leading-relaxed text-foreground/80 sm:text-lg lg:col-span-6">
          <p>
            We are gathering the people who will root Nia Ecoversity: anchor persons, wisdom councillors and key partners —
            and learners who want to walk the journey with us.
          </p>
          <p className="font-display text-2xl text-primary">
            We don't want only people who think the same as we do. A living ecology needs many voices.
          </p>
        </div>
      </div>
      <div className="mt-16 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {VOICES.map((v, i) => (
          <motion.div key={v.name} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.6, delay: i * 0.07 }}
            className="rounded-sm border border-border bg-card p-8 transition-transform duration-300 hover:-translate-y-1"
            data-testid={`voice-card-${v.name.split(" ")[1].toLowerCase()}`}>
            <v.icon className={`h-7 w-7 ${v.color}`} strokeWidth={1.5} />
            <h3 className="mt-5 font-display text-3xl font-bold text-foreground">{v.name}</h3>
            <p className="mt-1 text-xs font-semibold uppercase tracking-[0.18em] text-primary">{v.voice}</p>
            <p className="mt-4 text-sm leading-relaxed text-foreground/70">{v.text}</p>
          </motion.div>
        ))}
      </div>
      <p className="mt-6 text-xs text-foreground/55" data-testid="voices-credit">
        Roles adapted from "The 6 People You Need in Your Corner" by Jessica Hagy (Forbes).
      </p>
      <p className="mt-14 max-w-2xl text-base text-foreground/75">
        Which voice sounds like you? Learners: think, too, about who you'd invite to walk beside you — friends, family,
        knowledge-holders — as your own circle of support.
      </p>
      <JoinForm />
    </div>
  </section>
);
